import * as Sentry from '@sentry/react'
import { useRouter } from '@tanstack/react-router'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  GoogleDriveIcon,
  Input,
  SharePointIcon,
  WebCrawlerIcon
} from '@repo/ui'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { ConnectorType, addConnector, connectorLabel, saveConnectorConfig } from '@/api/content/connector'
import type { UserOrgProfile } from '@/api/user/profile'

export function ConnectorIcon({ type, className }: { type: string; className?: string }) {
  switch (type) {
    case ConnectorType.GoogleDrive:
      return <GoogleDriveIcon className={className} />
    case ConnectorType.SharePoint:
      return <SharePointIcon className={className} />
    case ConnectorType.WebCrawler:
      return <WebCrawlerIcon className={className} />
    default:
      return null
  }
}

export function AddConnectorDialog({
  open,
  userOrgProfile,
  projectId,
  onClose,
  onAdded,
  beforeOAuthRedirect
}: {
  open: boolean
  userOrgProfile: UserOrgProfile
  projectId?: number
  onClose: () => void
  onAdded?: () => void | Promise<void>
  beforeOAuthRedirect?: () => void
}) {
  const router = useRouter()
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [webCrawlerUrl, setWebCrawlerUrl] = useState<string>('')
  const [selectedConnectorType, setSelectedConnectorType] = useState<string>('')
  const [showWebCrawlerUrl, setShowWebCrawlerUrl] = useState<boolean>(false)
  const [connectorDescription, setConnectorDescription] = useState<string>('')

  function handleClose() {
    setConnectorDescription('')
    setSelectedConnectorType('')
    setWebCrawlerUrl('')
    setShowWebCrawlerUrl(false)
    setError(null)
    onClose()
  }

  function handleSelect(connectorType: string) {
    setError(null)
    setSelectedConnectorType(connectorType)
    setShowWebCrawlerUrl(connectorType === ConnectorType.WebCrawler)
  }

  async function handleAdd(connectorType: string) {
    if (selectedConnectorType === '') {
      setError('Please select a connector')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      if (connectorType === ConnectorType.WebCrawler) {
        if (webCrawlerUrl === '') {
          setError('URL cannot be empty')
          return
        }
        if (!URL.canParse(webCrawlerUrl)) {
          setError('Please enter a valid URL')
          return
        }
        await handleWebCrawlerConnectorAdd(webCrawlerUrl, connectorDescription, projectId)
        if (onAdded !== undefined) await onAdded()
        else await router.invalidate()
      }
      if (connectorType === ConnectorType.GoogleDrive || connectorType === ConnectorType.SharePoint) {
        const redirectUrl = await handleFileConnectorAdd(connectorType, connectorDescription, projectId)
        beforeOAuthRedirect?.()
        window.location.href = redirectUrl
      }
    } catch (err) {
      Sentry.captureException(err, {
        extra: { userId: userOrgProfile.uid, orgSlug: userOrgProfile.org.slug, connectorType, projectId }
      })
      setError(err instanceof Error ? err.message : 'An error occurred')
      return
    } finally {
      setSubmitting(false)
    }

    handleClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Connector</DialogTitle>
          <DialogDescription className="sr-only">Add Connector Modal</DialogDescription>
        </DialogHeader>
        <p className="text-sm">Please select the content connector you would like to add</p>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex flex-col gap-2">
          {userOrgProfile.enabled_features.g_drive_connector && (
            <AddConnectorDialogOption
              selectedConnectorType={selectedConnectorType}
              connectorType={ConnectorType.GoogleDrive}
              submitting={submitting}
              handleSelect={handleSelect}
            />
          )}
          {userOrgProfile.enabled_features.sharepoint_connector && (
            <AddConnectorDialogOption
              selectedConnectorType={selectedConnectorType}
              connectorType={ConnectorType.SharePoint}
              submitting={submitting}
              handleSelect={handleSelect}
            />
          )}
          {userOrgProfile.enabled_features.webcrawler_connector && (
            <AddConnectorDialogOption
              selectedConnectorType={selectedConnectorType}
              connectorType={ConnectorType.WebCrawler}
              submitting={submitting}
              handleSelect={handleSelect}
            />
          )}
        </div>
        {showWebCrawlerUrl && (
          <div className="flex flex-col gap-2">
            <p className="text-sm">Enter the website to retrieve</p>
            <Input
              placeholder="https://example.com"
              value={webCrawlerUrl}
              onChange={(e) => setWebCrawlerUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleAdd(selectedConnectorType)
              }}
            />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <p className="text-sm">Connector description (optional)</p>
          <Input
            placeholder="Enter a short description"
            onChange={(e) => setConnectorDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAdd(selectedConnectorType)
            }}
          />
        </div>
        <DialogFooter>
          <Button onClick={() => handleAdd(selectedConnectorType)}>
            <Plus className="size-4" />
            Add
          </Button>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

async function handleWebCrawlerConnectorAdd(
  webCrawlerUrl: string,
  connectorDescription: string,
  projectId?: number | undefined
) {
  const response = await addConnector(ConnectorType.WebCrawler, connectorDescription, projectId)

  await saveConnectorConfig(response.connectorId, projectId, undefined, { url: webCrawlerUrl })
}

async function handleFileConnectorAdd(
  connectorType: string,
  connectorDescription: string,
  projectId?: number | undefined
) {
  const response = await addConnector(connectorType, connectorDescription, projectId)

  const redirectUrl = new URL(response.redirectUrl)
  if (redirectUrl.protocol !== 'https:') {
    throw new Error('Redirect URL must use HTTPS')
  }

  return response.redirectUrl
}

function AddConnectorDialogOption({
  selectedConnectorType,
  connectorType,
  submitting,
  handleSelect
}: {
  selectedConnectorType: string
  connectorType: string
  submitting: boolean
  handleSelect: (connectorType: string) => void
}) {
  return (
    <Button
      variant={selectedConnectorType === connectorType ? 'default' : 'outline'}
      disabled={submitting}
      onClick={() => void handleSelect(connectorType)}
    >
      <ConnectorIcon type={connectorType} className="size-5" />
      <span>{connectorLabel(connectorType)}</span>
    </Button>
  )
}
